import { HTTPService } from './httpService';

const API = new HTTPService();

/**
 * Installer compliance documents — W9 and Certificate of Insurance.
 *
 * Modeled on logoService.js (installer-scoped multipart upload + matching delete), with
 * one deliberate departure: the logo is a PUBLIC S3 object whose URL is stored on the
 * installer record and cached in localStorage, because the navbar renders it. These
 * documents are NOT. A W9 carries a Taxpayer Identification Number — for a sole
 * proprietor, their SSN — so the file is private and reachable only through a
 * short-lived signed URL fetched at the moment of use.
 *
 * Never cache, store, or log the URL returned by getDocumentUrl.
 *
 * Access control is entirely server-side. getDealerSpecialistDocuments returns only the
 * specialists the calling dealer is entitled to, and the signed-URL endpoints re-check
 * that on every request — the client holds no gate logic.
 */

/** The two supported document types. */
export const DOC_TYPES = {
  W9: 'w9',
  COI: 'coi'
};

/** Display labels, so the UI doesn't hardcode strings per call site. */
export const DOC_LABELS = {
  [DOC_TYPES.W9]: 'W-9',
  [DOC_TYPES.COI]: 'Certificate of Insurance'
};

const InstallerDocumentService = {
  /**
   * Installer-side: which of their own documents exist, and when each was uploaded.
   * Presence + dates only — never S3 keys, never URLs.
   * @param {number} installerId
   * @returns {Promise<{w9?: {uploaded_at: string}, coi?: {uploaded_at: string}}>}
   */
  getDocuments: async (installerId) => {
    const response = await API.get(`/installer/${installerId}/documents`);
    return response.data || {};
  },

  /**
   * Upload (or replace) one document for an installer.
   * @param {number} installerId
   * @param {'w9'|'coi'} docType
   * @param {File} file - must be a PDF
   */
  uploadDocument: async (installerId, docType, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('docType', docType);
    const response = await API.post(`/installer/${installerId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Remove a document.
   * @param {number} installerId
   * @param {'w9'|'coi'} docType
   */
  deleteDocument: async (installerId, docType) => {
    const response = await API.delete(`/installer/${installerId}/documents/${docType}`);
    return response.data;
  },

  /**
   * Fetch a short-lived signed URL for one document.
   *
   * Call this at the moment the user asks to view — do NOT fetch ahead of time, hold the
   * result in state, or reuse it. The URL grants access to a private object and expires.
   *
   * @param {number} installerId
   * @param {'w9'|'coi'} docType
   * @returns {Promise<string>} the signed URL
   */
  getDocumentUrl: async (installerId, docType) => {
    const response = await API.get(`/installer/${installerId}/documents/${docType}/signed-url`);
    return response.data?.url || response.data?.signedUrl;
  },

  /**
   * Dealer-side: the specialists this dealer may retrieve documents for, with which
   * documents each has.
   *
   * The server computes eligibility (transacted with OR favorited) and returns only
   * permitted specialists. Render what comes back — do not filter or extend this list
   * client-side, or the UI and the enforced gate will drift apart.
   *
   * Response carries presence + upload dates only, never S3 keys.
   *
   * @param {number|null} dealerId - a specific dealership, or null/omitted for "all",
   *   in which case the server unions across every dealership in the caller's scope.
   *   Passed as a query param rather than a path segment precisely so that "all" is
   *   expressible — the client's selectedDealerId is a sentinel, not an id, when a
   *   group user is viewing all dealerships.
   */
  getDealerSpecialistDocuments: async (dealerId = null) => {
    const params = dealerId ? { dealerId } : {};
    const response = await API.get('/dealer/specialist-documents', params);
    return response.data || [];
  }
};

export default InstallerDocumentService;
